/**
 * Delivery Details Modal Component
 * Displays detailed information about a raw material delivery
 *
 * @author SAUBHAGYA Development Team
 * @since Raw Material Deliveries Module
 *
 * @description
 * Modal component showing:
 * - Route details (ID, name, status)
 * - Driver information (name, phone, vehicle)
 * - Statistics and notes
 *
 * Note: Uses data passed from parent component instead of making API call
 * since the backend doesn't have a dedicated delivery details endpoint.
 */

import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  Truck,
  User,
  Phone,
  Route as RouteIcon,
  Navigation,
  Timer,
  MapPin,
  Calendar,
  Loader2,
  CheckCircle2,
  Clock,
  SkipForward,
  Package,
  RefreshCw,
  DollarSign,
  FileText,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { DeliveryStatusBadge } from '../badges/DeliveryStatusBadge';
import { RawMaterialDeliveryDTO } from '@/services/manufacturing/rawMaterialDeliveryApi';
import { transportApi, type StopLog, type RawMaterialRoute } from '@/services/transport/api';
import { manufacturingApi, type TransactionDTO } from '@/services/manufacturing/manufacturingApi';
import LiveStopDetectionMap from '@/pages/transport/LiveStopDetectionMap';
import { toast } from '@/lib/toast';

interface DeliveryDetailsModalProps {
  delivery: RawMaterialDeliveryDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Format date time
 */
function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'PPp');
  } catch {
    return dateString;
  }
}

/**
 * Delivery Details Modal Component
 */
export function DeliveryDetailsModal({
  delivery,
  open,
  onOpenChange,
}: DeliveryDetailsModalProps) {
  const [stopLogs, setStopLogs] = useState<StopLog[]>([]);
  const [loadingStops, setLoadingStops] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('delivery-details');
  const [approvingTransactions, setApprovingTransactions] = useState(false);
  const [routeDetails, setRouteDetails] = useState<RawMaterialRoute | undefined>();
  const [pendingTransactions, setPendingTransactions] = useState<TransactionDTO[]>([]);

  // Fetch stop logs and route details when modal opens
  useEffect(() => {
    if (open && delivery?.todayExecutionId) {
      fetchStopLogs();
    } else if (!open) {
      setStopLogs([]);
      setRouteDetails(undefined);
    }
  }, [open, delivery?.todayExecutionId]);

  // Fetch route details for map coordinates
  useEffect(() => {
    if (open && delivery?.routeId) {
      transportApi.getRouteDetails(delivery.routeId)
        .then(setRouteDetails)
        .catch(() => setRouteDetails(undefined));
    }
  }, [open, delivery?.routeId]);

  // Reset tab when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab('delivery-details');
    }
  }, [open]);

  const fetchStopLogs = async () => {
    if (!delivery?.todayExecutionId) return;

    try {
      setLoadingStops(true);
      const logs = await transportApi.getExecutionStops(delivery.todayExecutionId);
      setStopLogs(logs);

      // Also fetch UP_COMING transactions to match with stop logs
      try {
        const transactionsResponse = await manufacturingApi.getTransactions({
          status: 'UP_COMING',
          size: 100, // Get all UP_COMING transactions
        });
        setPendingTransactions(transactionsResponse.content || []);
      } catch (txError) {
        console.error('Failed to fetch UP_COMING transactions:', txError);
        setPendingTransactions([]);
      }
    } catch (error) {
      console.error('Failed to fetch stop logs:', error);
      setStopLogs([]);
    } finally {
      setLoadingStops(false);
    }
  };

  const handleApproveAllTransactions = async () => {
    if (!stopLogs.length) return;

    try {
      setApprovingTransactions(true);

      // Filter only COMPLETED stops for approval
      const completedStops = stopLogs.filter(stop => stop.statusCode === 'COMPLETED');

      if (completedStops.length === 0) {
        toast.warning('There are no completed stops to approve.');
        return;
      }

      // Match completed stops with UP_COMING transactions by gaushalaId
      const transactionsToApprove: number[] = [];

      for (const stop of completedStops) {
        // Find matching UP_COMING transaction by gaushalaId
        const matchingTransaction = pendingTransactions.find(
          tx => tx.gaushala?.id === stop.gaushalaId || tx.gaushalaId === stop.gaushalaId
        );

        if (matchingTransaction) {
          transactionsToApprove.push(matchingTransaction.id);
        }
      }

      if (transactionsToApprove.length === 0) {
        toast.info('No matching UP_COMING transactions found for the completed stops. They may have already been approved.');
        return;
      }

      // Approve all matched transactions
      let successCount = 0;
      let failCount = 0;

      for (const transactionId of transactionsToApprove) {
        try {
          await manufacturingApi.approveDriverCollection(transactionId);
          successCount++;
        } catch (error) {
          console.error(`Failed to approve transaction ${transactionId}:`, error);
          failCount++;
        }
      }

      // Refresh data after approval
      await fetchStopLogs();

      // Show result
      if (failCount === 0) {
        toast.success(`Successfully approved ${successCount} transaction(s).`);
      } else {
        toast.warning(`Approved ${successCount} transaction(s), ${failCount} failed.`);
      }
    } catch (error) {
      console.error('Failed to approve transactions:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to approve transactions. Please try again.');
    } finally {
      setApprovingTransactions(false);
    }
  };

  // Check if there are completed stops that can be approved
  const completedStops = stopLogs.filter(stop => stop.statusCode === 'COMPLETED');

  // Count how many completed stops have matching UP_COMING transactions
  const matchingTransactionsCount = completedStops.filter(stop =>
    pendingTransactions.some(tx => tx.gaushala?.id === stop.gaushalaId || tx.gaushalaId === stop.gaushalaId)
  ).length;

  const canApproveAll = matchingTransactionsCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Delivery Details</span>
            {delivery && (
              <DeliveryStatusBadge
                status={delivery.status}
                statusName={delivery.statusName}
                size="lg"
                showPulse={delivery.status === 'IN_PROGRESS' || delivery.status === 'ACTIVE'}
              />
            )}
          </DialogTitle>
          <DialogDescription>
            {delivery
              ? `Route ${delivery.routeCode} - ${delivery.routeName}`
              : 'No delivery selected'}
          </DialogDescription>
        </DialogHeader>

        {delivery && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="delivery-details">Delivery Details</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
            </TabsList>

            {/* Delivery Details Tab - Keep existing content unchanged */}
            <TabsContent value="delivery-details" className="space-y-6 mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Route Info Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <RouteIcon className="h-4 w-4" />
                    Route Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Route Code:</span>
                    <p className="font-medium">{delivery.routeCode}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Route Name:</span>
                    <p className="font-medium">{delivery.routeName}</p>
                  </div>
                  {delivery.totalStops != null && (
                    <div>
                      <span className="text-muted-foreground">Total Stops:</span>
                      <p className="font-medium">{delivery.totalStops}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Driver Info Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Driver Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {delivery.driverName ? (
                    <>
                      <div>
                        <span className="text-muted-foreground">Name:</span>
                        <p className="font-medium">{delivery.driverName}</p>
                      </div>
                      {delivery.driverPhone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium">{delivery.driverPhone}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">No driver assigned</p>
                  )}
                </CardContent>
              </Card>

              {/* Vehicle Info Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Vehicle Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {delivery.vehicleNumber ? (
                    <>
                      <div>
                        <span className="text-muted-foreground">Vehicle:</span>
                        <p className="font-medium">{delivery.vehicleNumber}</p>
                      </div>
                      {delivery.vehicleType && (
                        <div>
                          <span className="text-muted-foreground">Type:</span>
                          <p className="font-medium">{delivery.vehicleType}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">No vehicle assigned</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Statistics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {delivery.totalDistanceKm != null && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <Navigation className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Distance</p>
                    <p className="font-semibold">{delivery.totalDistanceKm.toFixed(1)} km</p>
                  </div>
                </div>
              )}
              {delivery.estimatedDurationMin != null && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <Timer className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Est. Duration</p>
                    <p className="font-semibold">{delivery.estimatedDurationMin} min</p>
                  </div>
                </div>
              )}
              {/* Date - always shown using deliveryDate or createdAt as fallback */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                <Calendar className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-semibold">
                    {new Date(delivery.deliveryDate || delivery.createdAt).toLocaleDateString('en-IN', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              {delivery.totalStops != null && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Total Stops</p>
                    <p className="font-semibold">{delivery.totalStops}</p>
                  </div>
                </div>
              )}
              {/* Collected — actual kg gathered on today's execution. Hidden until a trip is in progress
                  or completed (otherwise renders as 0 and clutters the upcoming-route view). */}
              {delivery.actualQuantityKg != null && Number(delivery.actualQuantityKg) > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <Package className="h-5 w-5 text-indigo-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Collected</p>
                    <p className="font-semibold">{Number(delivery.actualQuantityKg).toFixed(1)} kg</p>
                  </div>
                </div>
              )}
              {/* Actual driven duration if available — beats the planned estimate after the trip ends. */}
              {delivery.actualDurationMin != null && delivery.actualDurationMin > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <Timer className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Actual Duration</p>
                    <p className="font-semibold">{delivery.actualDurationMin} min</p>
                  </div>
                </div>
              )}
              {delivery.manufacturingUnitName && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 col-span-2 md:col-span-2">
                  <Truck className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Mfg. Unit</p>
                    <p className="font-semibold text-sm break-words" title={delivery.manufacturingUnitName}>
                      {delivery.manufacturingUnitName}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Stop-by-Stop Progress */}
            {delivery.todayExecutionId && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Stop Progress (Live)
                  </h3>
                  <button
                    onClick={fetchStopLogs}
                    disabled={loadingStops}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${loadingStops ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {loadingStops ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-500">Loading stops...</span>
                  </div>
                ) : stopLogs.length > 0 ? (
                  <div className="space-y-2">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-green-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-green-700">
                          {stopLogs.filter(s => s.statusCode === 'COMPLETED').length}
                        </p>
                        <p className="text-xs text-green-600">Completed</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-orange-700">
                          {stopLogs.filter(s => s.statusCode === 'SKIPPED').length}
                        </p>
                        <p className="text-xs text-orange-600">Skipped</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-blue-700">
                          {stopLogs.reduce((sum, s) => sum + (s.quantityCollectedKg || 0), 0).toFixed(0)} kg
                        </p>
                        <p className="text-xs text-blue-600">Collected</p>
                      </div>
                    </div>

                    {/* Live Stop Detection Map */}
                    <LiveStopDetectionMap
                      stops={stopLogs}
                      mfgUnitLatitude={routeDetails?.mfgUnitLatitude}
                      mfgUnitLongitude={routeDetails?.mfgUnitLongitude}
                      mfgUnitName={routeDetails?.mfgUnitName || delivery?.manufacturingUnitName}
                    />

                    {/* Stop List */}
                    <div className="max-h-[250px] overflow-y-auto space-y-2">
                      {stopLogs.sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)).map((log, index) => {
                        const statusColor =
                          log.statusCode === 'COMPLETED' ? 'border-l-green-500 bg-green-50' :
                          log.statusCode === 'ARRIVED' ? 'border-l-blue-500 bg-blue-50' :
                          log.statusCode === 'SKIPPED' ? 'border-l-orange-500 bg-orange-50' :
                          'border-l-gray-300 bg-gray-50';

                        return (
                          <div
                            key={log.id}
                            className={`flex items-center gap-3 rounded-lg border-l-4 p-3 ${statusColor}`}
                          >
                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white border text-xs font-medium text-gray-600">
                              {log.sequenceOrder || index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">{log.gaushalaName}</p>
                              <p className="text-xs text-gray-500">ID: {log.gaushalaId}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {log.statusCode === 'COMPLETED' && log.quantityCollectedKg && (
                                <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded flex items-center gap-1">
                                  <Package className="h-3 w-3" />
                                  {log.quantityCollectedKg.toFixed(1)} kg
                                </span>
                              )}
                              {log.partitionLabel && (
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-blue-600 text-white">
                                  <Truck className="h-3 w-3" />
                                  {log.partitionLabel}
                                </span>
                              )}
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                                log.statusCode === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                log.statusCode === 'ARRIVED' ? 'bg-blue-100 text-blue-800' :
                                log.statusCode === 'SKIPPED' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {log.statusCode === 'COMPLETED' && <CheckCircle2 className="h-3 w-3" />}
                                {log.statusCode === 'ARRIVED' && <MapPin className="h-3 w-3" />}
                                {log.statusCode === 'SKIPPED' && <SkipForward className="h-3 w-3" />}
                                {log.statusCode === 'PENDING' && <Clock className="h-3 w-3" />}
                                {log.statusCode || 'PENDING'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p>No stop data available</p>
                  </div>
                )}
              </div>
            )}

            {!delivery.todayExecutionId && (
              <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4 text-center">
                <Clock className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-yellow-800 font-medium">No active execution</p>
                <p className="text-xs text-yellow-600">Driver has not started this route today</p>
              </div>
            )}

            <Separator />

            {/* Details Section */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Additional Details
              </h3>
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Route ID:</span>
                      <p className="font-medium">{delivery.routeId || delivery.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <p className="font-medium">{delivery.statusName || delivery.status}</p>
                    </div>
                    {delivery.manufacturingUnitId && (
                      <div>
                        <span className="text-muted-foreground">Manufacturing Unit ID:</span>
                        <p className="font-medium">{delivery.manufacturingUnitId}</p>
                      </div>
                    )}
                    {delivery.createdAt && (
                      <div>
                        <span className="text-muted-foreground">Created At:</span>
                        <p className="font-medium">{formatDateTime(delivery.createdAt)}</p>
                      </div>
                    )}
                    {delivery.startedAt && (
                      <div>
                        <span className="text-muted-foreground">Start Time:</span>
                        <p className="font-medium">{delivery.startedAt}</p>
                      </div>
                    )}
                    {delivery.completedAt && (
                      <div>
                        <span className="text-muted-foreground">Completed At:</span>
                        <p className="font-medium">{formatDateTime(delivery.completedAt)}</p>
                      </div>
                    )}
                    {delivery.notes && (
                      <div className="md:col-span-2">
                        <span className="text-muted-foreground">Notes:</span>
                        <p className="font-medium">{delivery.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            </TabsContent>

            {/* Transactions Tab - NEW */}
            <TabsContent value="transactions" className="space-y-4 mt-4">
              {loadingStops ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-500">Loading transaction data...</span>
                </div>
              ) : stopLogs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-gray-500 font-medium">No transaction data available</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Driver has not started this route or no stops have been completed
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                      <p className="text-2xl font-bold text-green-700">
                        {stopLogs.filter(s => s.statusCode === 'COMPLETED').length}
                      </p>
                      <p className="text-xs text-green-600 font-medium">Completed</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-200">
                      <p className="text-2xl font-bold text-orange-700">
                        {stopLogs.filter(s => s.statusCode === 'SKIPPED').length}
                      </p>
                      <p className="text-xs text-orange-600 font-medium">Skipped</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                      <p className="text-2xl font-bold text-blue-700">
                        {stopLogs.reduce((sum, s) => sum + (s.quantityCollectedKg || 0), 0).toFixed(0)} kg
                      </p>
                      <p className="text-xs text-blue-600 font-medium">Collected</p>
                    </div>
                  </div>

                  {/* Transaction Cards */}
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {stopLogs
                      .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
                      .map((stop, index) => {
                        const isCompleted = stop.statusCode === 'COMPLETED';
                        const isSkipped = stop.statusCode === 'SKIPPED';
                        const isPending = stop.statusCode === 'PENDING';
                        const isArrived = stop.statusCode === 'ARRIVED';

                        const borderColor = isCompleted
                          ? 'border-l-green-500'
                          : isSkipped
                          ? 'border-l-orange-500'
                          : isArrived
                          ? 'border-l-blue-500'
                          : 'border-l-gray-300';

                        const bgColor = isCompleted
                          ? 'bg-green-50/50'
                          : isSkipped
                          ? 'bg-orange-50/50'
                          : isArrived
                          ? 'bg-blue-50/50'
                          : 'bg-gray-50/50';

                        return (
                          <Card key={stop.id} className={`border-l-4 ${borderColor} ${bgColor}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-start gap-3 flex-1">
                                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white border-2 border-gray-300 text-sm font-bold text-gray-700">
                                    {stop.sequenceOrder || index + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <MapPin className="h-4 w-4 text-gray-600" />
                                      <h4 className="font-semibold text-gray-900">
                                        {stop.gaushalaName}
                                      </h4>
                                    </div>
                                    <p className="text-xs text-gray-500">Gaushala ID: {stop.gaushalaId}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {stop.partitionLabel && (
                                    <Badge
                                      variant="secondary"
                                      className="bg-blue-600 text-white border-blue-700"
                                    >
                                      <Truck className="h-3 w-3 mr-1 inline" />
                                      {stop.partitionLabel}
                                    </Badge>
                                  )}
                                  <Badge
                                    variant="secondary"
                                    className={`${
                                      isCompleted
                                        ? 'bg-green-100 text-green-800 border-green-200'
                                        : isSkipped
                                        ? 'bg-orange-100 text-orange-800 border-orange-200'
                                        : isArrived
                                        ? 'bg-blue-100 text-blue-800 border-blue-200'
                                        : 'bg-gray-100 text-gray-800 border-gray-200'
                                    }`}
                                  >
                                    {isCompleted && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                                    {isSkipped && <SkipForward className="h-3 w-3 mr-1 inline" />}
                                    {isArrived && <MapPin className="h-3 w-3 mr-1 inline" />}
                                    {isPending && <Clock className="h-3 w-3 mr-1 inline" />}
                                    {stop.statusCode || 'PENDING'}
                                  </Badge>
                                </div>
                              </div>

                              {isCompleted && stop.quantityCollectedKg && (
                                <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
                                  <Package className="h-5 w-5 text-green-600" />
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-500">Quantity Collected</p>
                                    <p className="text-lg font-bold text-green-700">
                                      {stop.quantityCollectedKg.toFixed(1)} kg
                                    </p>
                                  </div>
                                </div>
                              )}

                              {isSkipped && (
                                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                                  This stop was skipped during collection
                                </div>
                              )}

                              {isPending && (
                                <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                                  Pending collection
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>

                  {/* Approve All Transactions Button */}
                  {completedStops.length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <Button
                        onClick={handleApproveAllTransactions}
                        disabled={!canApproveAll || approvingTransactions}
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="lg"
                      >
                        {approvingTransactions ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Approving Transactions...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            Approve All Transactions ({matchingTransactionsCount})
                          </>
                        )}
                      </Button>
                      {!canApproveAll && completedStops.length > 0 && (
                        <p className="text-xs text-amber-600 text-center mt-2">
                          No pending transactions found for completed stops. They may have already been approved.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DeliveryDetailsModal;
